import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import BottomDrawer from '../../../../../../common/_custom/BottomDrawer/BottomDrawer';
import LoadingSpinner from '../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import SortIcon from '../../../../../Investor/Deals/components/DealsList/DealsTable/components/SortIcon/SortIcon';
import TimePeriodFilter from '../../../BankDataTable/components/TimePeriodFilter/TimePeriodFilter';
import TypeSelector from '../../../../components/TypeSelector/TypeSelector';
import { SortConfig } from '../../../../../../utils/constants/commonConstants';
import { nextSortConfig } from '../../../../../../utils/sortHeaderUtils';
import { Mixpanel } from '../../../../../../utils/mixpanel';
import { AICA_CASHFLOW_BANKING_TRANSACTIONS_SORTED } from '../../../../../../utils/constants/mixpanelEvents/investorEvents';
import { getLastCompletedMonthKey } from '../../../../../../utils/dateUtils';
import { BankTransaction } from './types';
import { buildDrillDownView, groupTransactionsByMonth } from './entityDrillDownGrouping';
import { EN_IN_INT } from './cashflowLayout';
import { DRILL_DOWN_SORT_ANALYTICS_COLUMNS, DRILL_DOWN_SORT_CLEARED, DRILL_DOWN_SORT_FIELDS } from './constants';
import { useDrillDownDateRange } from './useDrillDownDateRange';
import { CFB_FILTER_LOCATIONS, CFB_QUICK_RANGES, QUICK_RANGE_KEYS } from '../../../BankDataTable/constants';
import { createCfbFilterTracking } from '../../../BankDataTable/cfbUsageTracking';
import styles from './EntityDrillDownDrawer.module.scss';

interface EntityDrillDownDrawerProps {
    entityName: string;
    transactions: BankTransaction[];
    loading: boolean;
    onClose: () => void;
}

/** Plain en-IN formatted absolute value, rounded to whole rupees; '-' when not a finite number. No ₹ prefix. */
function formatTxnAmount(value: number | string): string {
    const n = typeof value === 'string' ? parseFloat(value) : value;
    if (!Number.isFinite(n)) return '-';
    return EN_IN_INT.format(Math.abs(n));
}

/** ₹-prefixed en-IN formatted absolute value, rounded to whole rupees, for subtotal/grand-total cells. */
function formatAmount(value: number): string {
    return `₹${EN_IN_INT.format(Math.abs(value))}`;
}

/** ₹-prefixed en-IN net amount, rounded to whole rupees, that preserves sign (negative nets show a leading '-'). */
function formatNetAmount(value: number): string {
    const amount = EN_IN_INT.format(Math.abs(value));
    return value < 0 ? `-₹${amount}` : `₹${amount}`;
}

function EntityDrillDownDrawer({ entityName, transactions, loading, onClose }: EntityDrillDownDrawerProps) {
    const grouped = useMemo(() => groupTransactionsByMonth(transactions), [transactions]);
    const { groups } = grouped;
    // Bounds for the pickers: the entity's own transaction span. Only read once `groups` is
    // non-empty — the loading/empty branches below return before this is ever wired to a picker.
    const datedMonthKeys = useMemo(() => groups.map((g) => g.monthKey).filter(Boolean), [groups]);
    const dateRangeBounds = {
        floor: datedMonthKeys[0] || getLastCompletedMonthKey(),
        ceiling: datedMonthKeys[datedMonthKeys.length - 1] || getLastCompletedMonthKey(),
    };
    const dateRange = useDrillDownDateRange(entityName, dateRangeBounds);
    const [sortConfig, setSortConfig] = useState<SortConfig | undefined>(undefined);
    const onSort = (field: string) => {
        const next = nextSortConfig(sortConfig, field);
        Mixpanel.track(AICA_CASHFLOW_BANKING_TRANSACTIONS_SORTED, {
            column: DRILL_DOWN_SORT_ANALYTICS_COLUMNS[field as keyof typeof DRILL_DOWN_SORT_ANALYTICS_COLUMNS],
            direction: (next?.direction ?? DRILL_DOWN_SORT_CLEARED).toLowerCase(),
        });
        setSortConfig(next);
    };

    const filterTracking = createCfbFilterTracking(CFB_FILTER_LOCATIONS.DRAWER, {
        quickRangeHandlers: {
            [QUICK_RANGE_KEYS.LAST_FY]: dateRange.selectLastFY,
            [QUICK_RANGE_KEYS.LAST_12M]: dateRange.selectLast12M,
            [QUICK_RANGE_KEYS.CURRENT_FY]: dateRange.selectCurrentFY,
        },
        activeQuickRange: dateRange.activeQuickRange,
        startMonth: dateRange.startMonth,
        endMonth: dateRange.endMonth,
        selectStartMonth: dateRange.selectStartMonth,
        selectEndMonth: dateRange.selectEndMonth,
    });

    const heading = (
        <span>
            <strong className={styles.DrawerHeading}>Transactions</strong>
            <span className={styles.DrawerHeadingName}>{` | ${entityName}`}</span>
        </span>
    );

    // Lives in the drawer's own header row; only shown once there's data to filter.
    const timePeriodActions = !loading && transactions.length > 0 && (
        <>
            <TimePeriodFilter
                startMonth={dateRange.startMonth}
                endMonth={dateRange.endMonth}
                startPickerMin={dateRange.startPickerMin}
                startPickerMax={dateRange.startPickerMax}
                endPickerMin={dateRange.endPickerMin}
                endPickerMax={dateRange.endPickerMax}
                rangeApplied={dateRange.rangeApplied}
                onSelectStartMonth={filterTracking.selectStartMonth}
                onSelectEndMonth={filterTracking.selectEndMonth}
                onClear={dateRange.clear}
            />
            <TypeSelector
                testId="drill-down-range-toggle"
                options={CFB_QUICK_RANGES}
                selectedOptionId={dateRange.activeQuickRange}
                changeSelectedOption={filterTracking.selectQuickRange}
            />
        </>
    );

    const view = useMemo(
        () => buildDrillDownView(grouped, { startMonth: dateRange.startMonth, endMonth: dateRange.endMonth }, sortConfig),
        [grouped, dateRange.startMonth, dateRange.endMonth, sortConfig],
    );

    return (
        <BottomDrawer
            heading={heading}
            headerActions={timePeriodActions}
            showHeading
            allowClose
            onClose={onClose}
            height="95%"
            style={{ maxHeight: '95%' }}
        >
            <div className={styles.DrawerContent}>
                {loading ? (
                    <LoadingSpinner height="40px" color="black" style={{ marginTop: '2rem' }} />
                ) : transactions.length === 0 ? (
                    <div className={styles.EmptyState}>No transactions found.</div>
                ) : (
                    <>
                        <div className={styles.TableWrap}>
                            <table className={styles.Table}>
                                <thead>
                                    <tr>
                                        <th>
                                            {/* onClick on the span, not just SortIcon: the label carries cursor:pointer, so it must be
                                                the click target too. No role/tabIndex — SortIcon is already the keyboard control. */}
                                            <span className={styles.SortableHeader} onClick={() => onSort(DRILL_DOWN_SORT_FIELDS.DATE)}>
                                                Txn. Date
                                                <SortIcon
                                                    field={DRILL_DOWN_SORT_FIELDS.DATE}
                                                    sortConfig={sortConfig}
                                                    onSort={onSort}
                                                    alwaysVisible
                                                />
                                            </span>
                                        </th>
                                        <th>
                                            <span className={styles.SortableHeader} onClick={() => onSort(DRILL_DOWN_SORT_FIELDS.CREDITS)}>
                                                Credits (₹)
                                                <SortIcon
                                                    field={DRILL_DOWN_SORT_FIELDS.CREDITS}
                                                    sortConfig={sortConfig}
                                                    onSort={onSort}
                                                    alwaysVisible
                                                />
                                            </span>
                                        </th>
                                        <th>
                                            <span className={styles.SortableHeader} onClick={() => onSort(DRILL_DOWN_SORT_FIELDS.DEBITS)}>
                                                Debit (₹)
                                                <SortIcon
                                                    field={DRILL_DOWN_SORT_FIELDS.DEBITS}
                                                    sortConfig={sortConfig}
                                                    onSort={onSort}
                                                    alwaysVisible
                                                />
                                            </span>
                                        </th>
                                        <th>Transactions Notes</th>
                                        <th>Tagged Entity</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {view.rows.map((row) =>
                                        row.kind === 'txn' ? (
                                            <tr key={`txn-${row.txn.id}`}>
                                                <td>{row.txn.date}</td>
                                                <td>{row.txn.transactionType !== 'debit' ? formatTxnAmount(row.txn.amount) : '-'}</td>
                                                <td>{row.txn.transactionType === 'debit' ? formatTxnAmount(row.txn.amount) : '-'}</td>
                                                <td>{row.txn.transactionNote}</td>
                                                <td>
                                                    {row.txn.taggedEntities?.length > 0
                                                        ? row.txn.taggedEntities.map((name) => (
                                                              <span key={name} className={styles.EntityChip}>
                                                                  {name}
                                                              </span>
                                                          ))
                                                        : null}
                                                </td>
                                            </tr>
                                        ) : (
                                            <tr key={`sub-${row.group.monthKey || row.group.monthLabel}`} className={styles.SubtotalRow}>
                                                <td>{`Total ${row.group.monthLabel}`}</td>
                                                <td>{formatAmount(row.group.credits)}</td>
                                                <td>{formatAmount(row.group.debits)}</td>
                                                <td>{`Net Amount: ${formatNetAmount(row.group.credits - row.group.debits)}`}</td>
                                                <td></td>
                                            </tr>
                                        ),
                                    )}
                                    <tr className={clsx(styles.SubtotalRow, styles.GrandTotalRow)}>
                                        <td>Grand Total</td>
                                        <td>{formatAmount(view.credits)}</td>
                                        <td>{formatAmount(view.debits)}</td>
                                        <td>{`Net Amount: ${formatNetAmount(view.net)}`}</td>
                                        <td></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </BottomDrawer>
    );
}

export default EntityDrillDownDrawer;
